// ConfirmDialog — the standard "are you sure?" pattern.
//
// Built on top of AppDialog. Exists to kill the eight slightly-different
// confirmation dialogs we have scattered across the app (delete user,
// delete connector, delete saved query, deactivate user, delete category,
// remove from dashboard, etc.).
//
// Three tones map to the three flavours of confirmation:
//
//   • neutral     — "Save and exit?", "Send invite?"  (primary blue)
//   • warning     — "This may take a few minutes"     (amber, soft icon)
//   • destructive — "Delete connector permanently"    (red + warning icon)
//
// For genuinely irreversible actions, pass `requireTypeToConfirm` to force
// the user to type a phrase (typically the name of the object) before the
// confirm button enables. This is the standard "type DELETE" pattern.

import React from 'react';
import { Box, Stack, Typography, TextField, Alert } from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import AppDialog, { AppDialogSize } from './AppDialog';

export type ConfirmTone = 'neutral' | 'warning' | 'destructive';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;

  title: string;
  /** Body copy. Plain string is rendered as a paragraph; node renders verbatim. */
  description?: React.ReactNode;
  /** Extra rich content rendered below the description (e.g. an Alert with detail). */
  body?: React.ReactNode;

  tone?: ConfirmTone;

  confirmLabel?: string;
  cancelLabel?: string;

  /** Locks the dialog while the async confirm runs. */
  busy?: boolean;

  /**
   * If set, renders a TextField; the confirm button is disabled until the
   * user types this phrase exactly (case-sensitive). Use the object's name
   * for the strongest "I really mean it" UX.
   */
  requireTypeToConfirm?: string;

  size?: AppDialogSize;
}

const TONE_CFG: Record<ConfirmTone, {
  icon?: React.ReactNode;
  primaryColor: 'primary' | 'error' | 'warning';
  alertSeverity?: 'warning' | 'error';
}> = {
  neutral: { primaryColor: 'primary' },
  warning: {
    icon: <WarningAmberRounded sx={{ color: 'warning.main' }} />,
    primaryColor: 'warning',
    alertSeverity: 'warning',
  },
  destructive: {
    icon: <ErrorOutlineRounded sx={{ color: 'error.main' }} />,
    primaryColor: 'error',
    alertSeverity: 'error',
  },
};

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  body,
  tone = 'neutral',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busy,
  requireTypeToConfirm,
  size = 'xs',
}: ConfirmDialogProps) {
  const cfg = TONE_CFG[tone];
  const [typed, setTyped] = React.useState('');

  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const typeMatches = !requireTypeToConfirm || typed === requireTypeToConfirm;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size={size}
      title={title}
      icon={cfg.icon}
      busy={busy}
      primaryAction={{
        label: confirmLabel,
        onClick: () => onConfirm(),
        color: cfg.primaryColor,
        disabled: !typeMatches,
        autoFocus: !requireTypeToConfirm,
      }}
      secondaryAction={{
        label: cancelLabel,
        onClick: onClose,
        color: 'inherit',
      }}
    >
      <Stack spacing={1.5}>
        {description && (
          typeof description === 'string' ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {description}
            </Typography>
          ) : (
            <Box>{description}</Box>
          )
        )}
        {body}
        {requireTypeToConfirm && (
          <Box>
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', mb: 0.75 }}>
              Type{' '}
              <Box
                component="span"
                sx={{
                  fontFamily: (t: any) => t.typography.fontFamilyMono || 'monospace',
                  fontWeight: 700,
                  px: 0.625,
                  py: 0.125,
                  borderRadius: 0.5,
                  bgcolor: (t) => (t.palette.mode === 'light' ? 'grey.100' : 'grey.900'),
                }}
              >
                {requireTypeToConfirm}
              </Box>{' '}
              to confirm.
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requireTypeToConfirm}
              disabled={busy}
              autoFocus
              inputProps={{ 'aria-label': 'Confirmation phrase' }}
            />
          </Box>
        )}
      </Stack>
    </AppDialog>
  );
}
